import React, { useState, useEffect, useCallback } from 'react';
import { Package, Calendar, Loader2, ChevronDown, ChevronUp, XCircle } from 'lucide-react';
import { useSelector } from 'react-redux';
import { getCourier } from '../../state/courierSlice';
import { getUser } from '../../state/authSlice';
import { apiConfig } from '../../shared/api-config';
import { showErrorToast, showSuccessToast } from '../../utils/toast';
import { useDarkMode } from '../../context/DarkModeContext';
import { ShopOrderApi, ShopOrder as ShopOrderModel } from '@shared/sdk-v1';

const ShopOrders: React.FC = () => {
  const { isDarkMode } = useDarkMode();
  const courier = useSelector(getCourier);
  const user = useSelector(getUser);

  // State
  const [orders, setOrders] = useState<ShopOrderModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null);

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    if (!user?.id || !courier?.id) return;

    setIsLoading(true);
    try {
      const orderApi = new ShopOrderApi(apiConfig());
      const data = await orderApi.shopOrderFind({
        filter: JSON.stringify({
          where: { customerId: user.id, courierId: courier.id },
          include: 'items',
          order: 'createdAt DESC'
        })
      });
      setOrders(data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      showErrorToast({ title: 'Error', message: 'Failed to load orders' });
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, courier?.id]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Friendly label maps
  const STATUS_LABELS: Record<string, string> = {
    pending: 'Processing',
    confirmed: 'Confirmed',
    paid: 'Payment Verified',
    ready: 'Ready for Pickup',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
  };

  const PAYMENT_STATUS_LABELS: Record<string, string> = {
    paid: 'Paid',
    unpaid: 'Unpaid',
    pending_verification: 'Payment Under Review',
    refunded: 'Refunded',
  };

  const getStatusLabel = (status?: string) => STATUS_LABELS[status?.toLowerCase() || ''] || status || 'Unknown';
  const getPaymentStatusLabel = (status?: string) => PAYMENT_STATUS_LABELS[status?.toLowerCase() || ''] || status || 'Unknown';

  // Get status badge color
  const getStatusBadge = (status?: string) => {
    const baseClasses = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';
    
    switch (status?.toLowerCase()) {
      case 'pending':
        return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200`;
      case 'confirmed':
        return `${baseClasses} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`;
      case 'paid':
        return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
      case 'ready':
        return `${baseClasses} bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200`;
      case 'delivered':
        return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
      case 'cancelled':
        return `${baseClasses} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200`;
    }
  };

  // Get payment status badge
  const getPaymentStatusBadge = (status?: string) => {
    const baseClasses = 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium';
    
    switch (status?.toLowerCase()) {
      case 'paid':
        return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
      case 'unpaid':
        return `${baseClasses} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
      case 'pending_verification':
        return `${baseClasses} bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200`;
      case 'pending':
        return `${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200`;
    }
  };

  // Cancel order
  const handleCancelOrder = async (orderId: number) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    try {
      const api = new ShopOrderApi(apiConfig());
      await api.shopOrderPrototypePatchAttributes({
        id: String(orderId),
        data: { status: 'cancelled' } as any,
      });
      showSuccessToast({ title: 'Success', message: 'Order cancelled' });
      fetchOrders();
      setExpandedOrderId(null);
    } catch {
      showErrorToast({ title: 'Error', message: 'Failed to cancel order' });
    }
  };

  // Format payment method
  const formatPaymentMethod = (method?: string) => {
    if (!method) return 'N/A';
    
    const methodMap: { [key: string]: string } = {
      'bank_transfer': 'Bank Transfer',
      'stripe': 'Stripe',
      'fygaro': 'Fygaro',
      'wipay': 'WiPay'
    };
    
    return methodMap[method] || method;
  };

  // Toggle order expansion
  const toggleOrder = (orderId: number) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto p-4 space-y-6 pb-6">
        {/* Orders List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary dark:text-blue-400" />
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12">
            <Package className="mx-auto h-16 w-16 text-gray-400 dark:text-gray-500" />
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No orders yet</h3>
            <p className="mt-2 text-gray-500 dark:text-gray-400">
              Start shopping to place your first order
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => {
              const isExpanded = expandedOrderId === order.id;
              
              return (
                <div
                  key={order.id}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden"
                >
                  {/* Order Header - Clickable */}
                  <div
                    onClick={() => toggleOrder(order.id!)}
                    className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <Calendar className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {order.createdAt ? new Date(order.createdAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            }) : 'N/A'}
                          </span>
                          <span className="text-gray-300 dark:text-gray-600">•</span>
                          <Package className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {order.items?.length || 0} {order.items?.length === 1 ? 'item' : 'items'}
                          </span>
                        </div>
                        
                        <p className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                          JMD ${order.totalAmount?.toFixed(2) || '0.00'}
                        </p>

                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={getStatusBadge(order.status)}>
                            {getStatusLabel(order.status)}
                          </span>
                          {order.paymentStatus && order.paymentStatus !== order.status && (
                            <span className={getPaymentStatusBadge(order.paymentStatus)}>
                              {getPaymentStatusLabel(order.paymentStatus)}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex-shrink-0 flex items-center justify-center min-w-[44px] min-h-[44px]">
                        {isExpanded ? (
                          <ChevronUp className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Order Details - Expandable */}
                  {isExpanded && (
                    <div className="border-t border-gray-200 dark:border-gray-700 p-4 space-y-4 bg-gray-50 dark:bg-gray-900/50">
                      {/* Order Info */}
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Order ID:</span>
                          <span className="font-medium text-gray-900 dark:text-white">#{order.id}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Payment Method:</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {formatPaymentMethod(order.paymentMethod)}
                          </span>
                        </div>
                        {order.notes && (
                          <div className="pt-2">
                            <span className="text-sm text-gray-600 dark:text-gray-400 block mb-1">Notes:</span>
                            <p className="text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-800 p-2 rounded border dark:border-gray-700">
                              {order.notes}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Items */}
                      <div className="space-y-3">
                        <h4 className="font-semibold text-gray-900 dark:text-white text-sm">Items</h4>
                        {order.items?.map((item, index) => (
                          <div 
                            key={index} 
                            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3"
                          >
                            <div className="flex justify-between items-start gap-3">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 dark:text-white">{item.productName}</p>
                                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                  Qty: {item.quantity} × JMD ${item.price?.toFixed(2) || '0.00'}
                                </p>
                              </div>
                              <p className="font-semibold text-gray-900 dark:text-white flex-shrink-0">
                                JMD ${((item.price || 0) * (item.quantity || 1)).toFixed(2)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Status Timeline */}
                      <div className="pt-2 border-t dark:border-gray-700">
                        <h4 className="font-semibold text-gray-900 dark:text-white text-sm mb-3">Order Status</h4>
                        <div className="space-y-2">
                          {order.status?.toLowerCase() === 'cancelled' ? (
                            <div className="flex items-center gap-3">
                              <div className="w-3 h-3 rounded-full flex-shrink-0 bg-red-500 ring-4 ring-red-500/20" />
                              <span className="text-sm text-gray-900 dark:text-white font-medium">Cancelled</span>
                            </div>
                          ) : (
                            (['pending', 'confirmed', 'paid', 'ready', 'delivered'] as const).map((step) => {
                              const steps = ['pending', 'confirmed', 'paid', 'ready', 'delivered'];
                              const statusIndex = steps.indexOf(order.status?.toLowerCase() || 'pending');
                              const currentIndex = steps.indexOf(step);
                              const isCompleted = currentIndex <= statusIndex;
                              const isCurrent = step === order.status?.toLowerCase();
                              
                              return (
                                <div key={step} className="flex items-center gap-3">
                                  <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                                    isCurrent
                                      ? 'bg-primary ring-4 ring-primary/20'
                                      : isCompleted
                                        ? 'bg-green-500'
                                        : 'bg-gray-300 dark:bg-gray-600'
                                  }`} />
                                  <span className={`text-sm ${
                                    isCurrent || isCompleted
                                      ? 'text-gray-900 dark:text-white font-medium'
                                      : 'text-gray-500 dark:text-gray-400'
                                  }`}>
                                    {getStatusLabel(step)}
                                  </span>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>

                      {/* Payment Proof */}
                      {order.proofOfPayment && (
                        <div className="pt-2 border-t dark:border-gray-700">
                          <h4 className="font-semibold text-gray-900 dark:text-white text-sm mb-2">Payment Proof</h4>
                          <a
                            href={`${import.meta.env.VITE_API_BASE_URL}/Uploads/shop-proof-${courier?.id}/download/${order.proofOfPayment}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary dark:text-blue-400 hover:underline"
                          >
                            View proof of payment
                          </a>
                        </div>
                      )}

                      {/* Cancel button for pending orders */}
                      {order.status?.toLowerCase() === 'pending' && (
                        <div className="pt-2 border-t dark:border-gray-700">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleCancelOrder(order.id!); }}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                          >
                            <XCircle className="w-4 h-4" />
                            Cancel Order
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopOrders;
